import { addDays, dateNowUtc, formatDateFullDay, isAfter } from '~/services/TimeService';

type AvailabilityDate = Date | string;

interface AvailabilityBlock {
  range: [AvailabilityDate, AvailabilityDate];
}

interface DateFlagBlock {
  start_date: AvailabilityDate;
  end_date: AvailabilityDate;
  restriction_type?: string;
  [dayOfWeek: string]: unknown;
}

function dateIsInAvailabilityBlock(date: AvailabilityDate, block: AvailabilityBlock): boolean {
  const startDate = dateNowUtc(block.range[0]);
  if (isAfter(startDate, date)) {
    return false;
  }
  const endDate = addDays(1, dateNowUtc(block.range[1]));
  return isAfter(endDate, date);
}

function dateIsInBlackoutOrSurchargeBlock(date: AvailabilityDate, block: DateFlagBlock): boolean {
  const startDate = dateNowUtc(block.start_date);
  if (isAfter(startDate, date)) {
    return false;
  }

  const endDate = addDays(1, dateNowUtc(block.end_date));
  if (!isAfter(endDate, date)) {
    return false;
  }

  const dayOfWeek = formatDateFullDay(date).toLowerCase();
  return Boolean(block[dayOfWeek]);
}

export function dateIsBlackout(date?: AvailabilityDate | null, blackoutDates?: Array<DateFlagBlock> | null): boolean {
  if (!date || !blackoutDates) {
    return false;
  }
  return !!blackoutDates.find((blackout) => dateIsInBlackoutOrSurchargeBlock(date, blackout));
}

export function getBlackoutRestrictionTypeForDate(
  date?: AvailabilityDate | null,
  blackoutDates?: Array<DateFlagBlock> | null,
): string | false {
  if (!date || !blackoutDates) {
    return false;
  }

  const blackout = blackoutDates.find((blackout) => dateIsInBlackoutOrSurchargeBlock(date, blackout));

  if (blackout) {
    return blackout.restriction_type;
  }

  return false;
}

export function dateHasSurcharge<TBlock extends DateFlagBlock>(
  date?: AvailabilityDate | null,
  surchargeDates?: Array<TBlock> | null,
): TBlock | null | undefined {
  if (!date || !surchargeDates) {
    return null;
  }
  return surchargeDates.find((surcharge) => dateIsInBlackoutOrSurchargeBlock(date, surcharge));
}

export function getAvailabilityForDate<TBlock extends AvailabilityBlock>(
  date?: AvailabilityDate | null,
  availability?: Array<TBlock> | null,
): TBlock | null | undefined {
  if (!date || !availability) {
    return null;
  }
  return availability.find((block) => dateIsInAvailabilityBlock(date, block));
}
